/**
 * Shared contract for dragging Marcus items (documents and folders) onto drop
 * zones. One source of truth for the DataTransfer MIME types, the serialized
 * payload shape, and the parser — reused by the drag source (useBrowser) and
 * every drop target (DocumentPicker, chat input, future zones).
 */

/** Multi-item payload (preferred) first, single-item payload (legacy) second. */
export const MARCUS_DROP_MIME = {
  items: 'application/x-marcus-items',
  item: 'application/x-marcus-item',
} as const

/** Minimal item descriptor placed on the DataTransfer at drag start. */
export interface DroppedItem {
  id: string
  type: 'document' | 'folder' | string
  name?: string
  file_name?: string | null
  contract_type?: string | null
  /** Server-derived identity-slot eligibility (MAR-168), when the drag source knew it. */
  has_identity_data?: boolean | null
  /** Parent folder path (e.g. '/' or '/clientes'). Carried so folders can be
   *  resolved to their descendant documents on drop. */
  path?: string
  /** Folder slug; combined with `path` to form the folder's full path
   *  (`fullPathOf`, `shared/items/itemPath.ts`). */
  slug?: string
}

type DropTypes = readonly string[] | DOMStringList | null | undefined

/** True if a DataTransfer carries a Marcus item payload. */
export function hasMarcusPayload(types: DropTypes): boolean {
  if (!types) return false
  const list = Array.from(types)
  return list.includes(MARCUS_DROP_MIME.items) || list.includes(MARCUS_DROP_MIME.item)
}

/**
 * True if a DataTransfer carries OS files — the other kind of drop every zone tells apart from a
 * Marcus payload. The ONE copy (MAR-948): it was written out seven times, in three spellings.
 */
export function hasNativeFiles(types: DropTypes): boolean {
  return !!types && Array.from(types).includes('Files')
}

/** Parse the dragged Marcus items off a DataTransfer (multi first, then single). */
export function parseDropPayload(dt: DataTransfer | null | undefined): DroppedItem[] {
  if (!dt) return []
  const rawItems = dt.getData(MARCUS_DROP_MIME.items)
  if (rawItems) {
    try {
      const parsed = JSON.parse(rawItems)
      return Array.isArray(parsed) ? parsed : []
    } catch { return [] }
  }
  const rawItem = dt.getData(MARCUS_DROP_MIME.item)
  if (rawItem) {
    try { return [JSON.parse(rawItem)] } catch { return [] }
  }
  return []
}
